import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
  signInWithPopup,
  signOut as fbSignOut,
} from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const REGION = import.meta.env.VITE_FUNCTIONS_REGION || "europe-west1";

export const app = initializeApp(config);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, REGION);

if (import.meta.env.VITE_USE_EMULATORS === "true") {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
}

/**
 * URL of a callable served through the gateway on the `oauth` function
 * (functions/src/api/gateway.ts) -- the workaround for callables whose own service is
 * missing its public invoker binding. httpsCallableFromURL ignores
 * connectFunctionsEmulator, so the emulator address has to be built by hand here.
 */
export function gatewayUrl(name: string): string {
  if (import.meta.env.VITE_USE_EMULATORS === "true") {
    return `http://127.0.0.1:5001/${config.projectId}/${REGION}/oauth/api/${name}`;
  }
  return `https://${REGION}-${config.projectId}.cloudfunctions.net/oauth/api/${name}`;
}

/**
 * Survive a refresh. Without this the user is bounced to the login screen every time
 * they reload, which is especially grating right after the OAuth popup closes.
 */
void setPersistence(auth, browserLocalPersistence);

const google = new GoogleAuthProvider();
google.setCustomParameters({ prompt: "select_account" });

export function signInWithGoogle() {
  return signInWithPopup(auth, google);
}

export function signOut() {
  return fbSignOut(auth);
}
